/* eslint-disable */
import type {
  BackgroundBlurConfig,
  BackgroundFilterType,
  ColorFilterConfig,
  IBaseAdapter,
  SupportInfo,
  VideoBackgroundConfig,
  VirtualBackgroundConfig
} from '../core/types'

/**
 * Abstract base class that every video-platform adapter must extend.
 *
 * Defines the contract for attaching to a publisher, managing background
 * and color filters, querying feature support, and cleanup. Implement a
 * concrete subclass (e.g. {@link OpenTokAdapter}) for each video
 * provider you need to support.
 */
export abstract class BaseAdapter implements IBaseAdapter {
  /** Wire the processing pipeline to the provider's publisher object. */
  abstract attachToPublisher (publisher: any): Promise<void>

  /** Disconnect the pipeline and release the publisher reference. */
  abstract detachFromPublisher (): Promise<void>

  /** Enable Gaussian background blur with the given radius. */
  abstract enableBackgroundBlur (config: BackgroundBlurConfig): Promise<void>

  /** Replace the background with a static image. */
  abstract setVirtualBackground (config: VirtualBackgroundConfig): Promise<void>

  /** Replace the background with a looping video. */
  abstract setVideoBackground (config: VideoBackgroundConfig): Promise<void>

  /** Remove the active background filter, if any. */
  abstract disableBackground (): Promise<void>

  /** Return the type of background filter currently active, or `null`. */
  abstract getActiveBackgroundType (): BackgroundFilterType

  /** Apply color adjustments to the video stream. */
  abstract applyColorFilter (config: ColorFilterConfig): Promise<void>

  /** Remove all color adjustments. */
  abstract removeColorFilter (): Promise<void>

  /** Return a copy of the active color config, or `null` if none. */
  abstract getActiveColorConfig (): ColorFilterConfig | null

  /** Query per-feature browser support. */
  abstract isSupported (): SupportInfo

  /** Human-readable name for this adapter (e.g. `'opentok'`). */
  abstract getName (): string

  /** Tear down all resources held by the adapter. */
  abstract destroy (): Promise<void>
}
